// Service Worker Registration Helper

// Callback to notify app of updates
let updateCallback = null;
let lastUpdateNotification = 0;
const UPDATE_NOTIFICATION_COOLDOWN = 5000; // Don't spam updates within 5 seconds

export function setUpdateCallback(callback) {
  updateCallback = callback;
}

// Helper to get version from a service worker
async function getWorkerVersion(worker) {
  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();
    messageChannel.port1.onmessage = (event) => {
      resolve(event.data.version || null);
    };
    worker.postMessage({ type: 'GET_VERSION' }, [messageChannel.port2]);
    
    // Timeout after 1 second
    setTimeout(() => resolve(null), 1000);
  });
}

export function register() {
  // Disable service worker in development to prevent endless reload loops with HMR
  if (process.env.NODE_ENV === 'development') {
    console.log('[ServiceWorker] Disabled in development mode to prevent HMR conflicts');
    return;
  }
  
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      navigator.serviceWorker
        .register(swUrl)
        .then((registration) => {
          console.log('[ServiceWorker] Registration successful:', registration.scope);
          
          // Only enable aggressive update checking in production
          if (process.env.NODE_ENV === 'production') {
            // Check for updates immediately on visibility change
            let lastUpdateCheck = Date.now();
            document.addEventListener('visibilitychange', () => {
              if (!document.hidden && Date.now() - lastUpdateCheck > 60000) {
                lastUpdateCheck = Date.now();
                registration.update();
              }
            });
            
            // Check for updates every 5 minutes
            setInterval(() => {
              registration.update();
            }, 300000);
          } else {
            console.log('[ServiceWorker] Development mode - automatic update checks disabled');
          }
          
          registration.onupdatefound = () => {
            const installingWorker = registration.installing;
            if (installingWorker == null) {
              return;
            }
            installingWorker.onstatechange = async () => {
              if (installingWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // Wait a moment for the state to settle
                  setTimeout(async () => {
                    // Verify there's actually a waiting worker
                    if (!registration.waiting) {
                      console.log('[ServiceWorker] No waiting worker, already on latest version.');
                      return;
                    }
                    
                    try {
                      // Get versions from both workers
                      const activeVersion = await getWorkerVersion(registration.active);
                      const waitingVersion = await getWorkerVersion(registration.waiting);
                      
                      console.log('[ServiceWorker] Active version:', activeVersion, 'Waiting version:', waitingVersion);
                      
                      // Only notify if versions are different
                      if (waitingVersion && activeVersion !== waitingVersion) {
                        // Check cooldown to prevent rapid-fire notifications
                        const now = Date.now();
                        if (now - lastUpdateNotification < UPDATE_NOTIFICATION_COOLDOWN) {
                          console.log('[ServiceWorker] Update cooldown active, skipping notification');
                          return;
                        }
                        
                        lastUpdateNotification = now;
                        console.log('[ServiceWorker] New version available:', waitingVersion);
                        localStorage.setItem('app-version', activeVersion || 'unknown');
                        if (updateCallback) {
                          updateCallback(registration);
                        }
                      } else {
                        console.log('[ServiceWorker] Same version, no update needed.');
                      }
                    } catch (error) {
                      console.error('[ServiceWorker] Error checking versions:', error);
                    }
                  }, 100);
                } else {
                  console.log('[ServiceWorker] Content cached for offline use.');
                }
              }
            };
          };
        })
        .catch((error) => {
          console.error('[ServiceWorker] Registration failed:', error);
        });
    });
  }
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

export function skipWaiting() {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
  }
}
